import EditableDropdown from '../../../../common/EditableDropdown/EditableDropdown';
import styles from './AuditedFinancialsVisualisation.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import { fetchMisTableOptions } from '../../../../store/company/action';
import { useDispatch } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import ClassifiedTable from '../../components/Tables/ClassifiedTable/ClassifiedTable';
import expandMore from '../../../../assets/expand-more.svg';
import infoIcon from '../../../../assets/info.svg';
import editClassificationIcon from './editClassificationIcon.svg';
import { ctaPurpleRight } from '../../../../assets/hostedassets';
import RawTable from '../../components/Tables/RawTable/RawTable';
import { isEmpty } from '../../../../utils/utils';
import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE } from '../../../../utils/constants/commonConstants';
import FinancialLoaderComponent from '../../../../common/FinancialLoader/FinancialLoaderComponent';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import A4Page from '../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../store/pdfReport/pdfReport_constants';
import { useMcaPolling } from '../../../../utils/customHooks';
import AuditorCommentsPill from '../../../../common/AuditorCommentsPill/AuditorCommentsPill';
import clsx from 'clsx';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { useAuditedType } from './useAuditedType';
import ThreeDotMenu from '../../../../common/ThreeDotMenu/ThreeDotMenu';
import CategorySelector from '../../../../common/CategorySelector/CategorySelector';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import FileViewerDrawer from '../../../../common/DocumentViewer/FileViewerDrawer';
import { useAuditedReportDownload } from '../AuditedFinancials/useAuditedReportDownload';
import { useAuditedStatementData } from './useAuditedStatementData';
import { AUDITED_STATEMENT_CONFIGS, AUDITED_STATEMENT_FALLBACK_LABELS } from './auditedStatementTypes';
import AuditedStatementSection from './components/AuditedStatementSection/AuditedStatementSection';
import {
    BALANCE_SHEET_LAYOUT,
    PROFIT_AND_LOSS_LAYOUT,
    CASH_FLOW_LAYOUT,
    AUDITED_UNIT_DEFAULT,
    AUDITED_UNIT_OPTIONS,
} from './auditedStatementLayout';
import { DATE_FORMATS, formatDateByFormat } from '../../../../utils/dateUtils';

const AuditedFinancialsVisualisation = ({ selectedMetric = '', isPDFReport = false, assesseeOrgId = '', companyName = '' }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParams = useParams();

    const [rawOptions, setRawOptions] = useState<any[]>([]);
    const [metricOptions, setMetricOptions] = useState<string[]>([]);
    const [metricOptionsObject, setMetricsOptionsObject] = useState<any[]>([]);

    const orgId = queryParams.id || assesseeOrgId;
    const { mcaDocData } = useMcaPolling(orgId);
    const isMcaCalled = mcaDocData?.isFetching === true;

    const fetchOptions = () => {
        dispatch(
            fetchMisTableOptions({
                orgId: orgId || '',
                source: 'AicaAuditedFinancials',
                onSuccess: (res: any) => {
                    const optionsData = res?.data?.data;
                    const misOptionsBlock = optionsData?.[0];
                    setRawOptions(misOptionsBlock?.misOptions);
                    setMetricOptions(misOptionsBlock?.misOptions?.map((item: any) => item.misDisplayName) ?? []);
                    setMetricsOptionsObject(misOptionsBlock?.misOptions || []);

                    const consolidationBlock = optionsData?.find(
                        (block: any) => block?.latestConsolidatedFY !== undefined || block?.latestStandaloneFY !== undefined,
                    );
                    setAuditedTypeFromYears(
                        consolidationBlock?.latestConsolidatedFY ?? null,
                        consolidationBlock?.latestStandaloneFY ?? null,
                    );
                },
            }),
        );
    };

    useEffect(() => {
        orgId && fetchOptions();
    }, [orgId]);

    useEffect(() => {
        if (metricOptions.includes('Other Metric (MIS)')) {
            setMetricOptions(metricOptions.filter((item) => item !== 'Other Metric (MIS)'));
        }
    }, [metricOptions]);

    // ── Redesign — three statements resolved from the tab options, fixed Figma order ──
    const resolvedMetrics = AUDITED_STATEMENT_CONFIGS.reduce(
        (acc, { key, matcher }) => {
            acc[key] = metricOptions.find((option) => matcher.test(option)) ?? '';
            return acc;
        },
        {} as Record<string, string>,
    );

    const balanceSheet = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : resolvedMetrics.balanceSheet });
    const profitAndLoss = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : resolvedMetrics.profitAndLoss });
    const cashFlow = useAuditedStatementData({ orgId, metric: isPDFReport ? '' : resolvedMetrics.cashFlow });

    // ── Legacy single-metric report mode (AicaPDFReport, driven by `selectedMetric`) ──
    const [reportMetric, setReportMetric] = useState<string>('');
    useEffect(() => {
        if (!isPDFReport) return;
        if (metricOptionsObject?.length && metricOptions?.length) {
            if (selectedMetric) setReportMetric(selectedMetric);
            else {
                const first = (metricOptionsObject as any[]).find((item) => item?.isDataAvailable === true)?.misDisplayName;
                setReportMetric(first || metricOptions?.[0]);
            }
        }
    }, [isPDFReport, metricOptionsObject, metricOptions, selectedMetric]);

    const reportModeSection = useAuditedStatementData({ orgId, metric: isPDFReport ? reportMetric : '' });

    // ── Consolidation (Standalone / Consolidated) — shared across every statement ──
    const onConsolidationChange = (nature: string) => {
        [balanceSheet, profitAndLoss, cashFlow, reportModeSection].forEach((section) => {
            section.resetDrillDown();
            section.fetchTableData(nature);
        });
    };

    const { auditedType, auditedTypeOptionsObject, handleConsolidationChange, setAuditedTypeFromYears } =
        useAuditedType(onConsolidationChange);

    // Units selector (RECUR-27010): INR (default — raw) / Lakhs / Crore. Rescaling happens in the table.
    const [unit, setUnit] = useState<string>(AUDITED_UNIT_DEFAULT);

    // "Raw Data" download/view hook — lifted here so BOTH the full-width inline button and the
    // compact kebab item can trigger it, with the viewer rendered once at page level.
    const { downloading, viewLoading, viewerState, onViewClick, onDownloadClick, closeViewer } = useAuditedReportDownload({
        assesseeOrgId: orgId,
        orgName: companyName,
    });

    // UAT RECUR-26713 (Nishank #20/#21/#22): the inline controls are correct at full width; when
    // the Comments drawer opens and narrows the page, the secondary actions collapse into a 3-dot
    // menu and the last-updated date becomes a hover tooltip. Drive that off the page width so it's
    // decoupled from the drawer internals (full ≈ 1288px, drawer-open ≈ 938px → threshold 1050px).
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [isCompact, setIsCompact] = useState(false);
    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? el.clientWidth;
            setIsCompact(width < 1050);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // Truncate the FY column set for the printed report page (unchanged legacy behavior).
    useEffect(() => {
        if (!isPDFReport) return;
        const tableHeadersLength = reportModeSection.tableData?.headers?.length;
        if (!tableHeadersLength) return;

        if (tableHeadersLength > 1) {
            const headers = reportModeSection.tableData.headers;
            const headersForPDFReport = [
                headers[0],
                ...(tableHeadersLength > 3 ? [headers[tableHeadersLength - 3]] : []),
                ...(tableHeadersLength > 2 ? [headers[tableHeadersLength - 2]] : []),
                headers[tableHeadersLength - 1],
            ];

            if (JSON.stringify(headers) !== JSON.stringify(headersForPDFReport)) {
                reportModeSection.setTableData((data: any) => ({ ...data, headers: headersForPDFReport }));
            }
        }
        // reportModeSection.setTableData is a stable state-dispatch setter off the useReportMode
        // hook — safe to omit; depending on the whole hook object would re-run this on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPDFReport, reportModeSection.tableData?.headers]);

    const showAllFinancialMetrics = () => {
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    // ── Legacy report-mode render — unchanged from the pre-redesign single-metric view ──
    if (isPDFReport) {
        const { tableData, loading, tableType, setTableType, rawTableData, isEditing, setIsEditing, changedData, setChangedData, fetchRawData, handleSave } =
            reportModeSection;
        const hasData = !isEmpty(tableData?.headers);

        return tableData?.headers?.length ? (
            <A4Page sectionName={REPORT_SECTION_NAMES.AUDITED_FINANCIALS}>
                <div className={styles.Card}>
                    <div className={styles.Top}>
                        <div className={styles.Title}>
                            Financial Statements&nbsp;
                            <span className={styles.Secondary}>| {reportMetric}</span>
                        </div>
                        <div className={styles.Right}>All values are in INR</div>
                    </div>
                    <div className={styles.Body}>
                        <div className={styles.HeadingContainer}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <h3>Financial Statements</h3>
                                <div className={styles.divider} />
                                <EditableDropdown
                                    id={'MetricSelector'}
                                    optionStyle={{ fontSize: '14px' }}
                                    value={reportMetric}
                                    options={metricOptions}
                                    setOutput={(val) => setReportMetric(val)}
                                    removeInput={true}
                                    placeHolder={'Select Metric'}
                                    optionsObject={metricOptionsObject}
                                />
                            </div>
                        </div>

                        {tableType && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    margin: '0.5rem 0',
                                    padding: '0 0.5rem',
                                }}
                            >
                                <div className={styles.Breadcrumb}>
                                    <span className={styles.Blue} onClick={() => setTableType('')}>
                                        {reportMetric}
                                    </span>
                                    <img src={expandMore} alt="exp" height="10px" />
                                    {tableType}
                                </div>
                                {isEditing ? (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            fontFamily: 'Gilroy-Semibold,serif',
                                            fontSize: '14px',
                                        }}
                                    >
                                        <div>Save Classification</div>
                                        <button
                                            onClick={() => {
                                                setIsEditing(false);
                                                setChangedData([]);
                                            }}
                                            className={styles.CancelClassification}
                                        >
                                            Cancel
                                        </button>
                                        <button onClick={handleSave} className={styles.SaveClassification}>
                                            <img
                                                src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/saveClassificationInvestor.svg'}
                                                alt={'save'}
                                            />
                                            Save
                                        </button>
                                    </div>
                                ) : (
                                    <div onClick={() => setIsEditing(true)} className={styles.EditClassification}>
                                        <img src={'https://fl-fe-assets.s3.ap-south-1.amazonaws.com/svg/editClassification.svg'} alt="edit" />
                                        Edit Classification
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={clsx(styles.TableContainer, { [styles.NoTableData]: !hasData })}>
                            {tableType && (
                                <RawTable
                                    data={rawTableData}
                                    loading={loading}
                                    freezeColumnEnd={2}
                                    freezeColumnStart={0}
                                    isEditing={isEditing}
                                    metric={reportMetric}
                                    rawOptions={rawOptions}
                                    changedData={changedData}
                                    setChangedData={setChangedData}
                                />
                            )}
                            {tableType === '' && hasData && (
                                <ClassifiedTable
                                    data={tableData}
                                    loading={false}
                                    handleClick={fetchRawData}
                                    metric={reportMetric}
                                    isPDFReport={isPDFReport}
                                    initialHorizontalScroll="right"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </A4Page>
        ) : (
            <></>
        );
    }

    // ── Redesign — single scrolling page, three collapsible sections (RECUR-27010) ──
    const lastUpdatedAt = mcaDocData?.updatedAt;

    return (
        <div className={styles.AuditedRoot} ref={rootRef}>
            <div className={styles.MetaRow}>
                <div className={styles.MetaLeft}>
                    <h2 className={styles.PageTitle}>Audited Financials</h2>
                    <div className={styles.UnitsSelect}>
                        <span className={styles.UnitsLabel}>Units ₹:</span>
                        <EditableDropdown
                            id="UnitsSelector"
                            value={unit}
                            options={AUDITED_UNIT_OPTIONS}
                            setOutput={(val: string) => setUnit(val)}
                            removeInput={true}
                            optionStyle={{ fontSize: '14px' }}
                            placeHolder={AUDITED_UNIT_DEFAULT}
                        />
                    </div>
                </div>
                {/* UAT RECUR-26713 (Nishank): the fetching message, last-updated, and Source sit
                    together on one line below the title. Last Updated is an info icon whose hover
                    tooltip shows the date (declutters; the raw date isn't needed at a glance). */}
                <div className={styles.MetaInfo}>
                    {isMcaCalled && (
                        <FinancialLoaderComponent tableName="" message="Fetching latest data from MCA (takes upto 24 hours)" status />
                    )}
                    {lastUpdatedAt && (
                        <Tooltip
                            content={`Last Updated on ${formatDateByFormat(lastUpdatedAt, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY)}`}
                            direction="top"
                            usePortal
                        >
                            <img className={styles.LastUpdatedInfo} src={infoIcon} alt="Last updated" width={16} height={16} />
                        </Tooltip>
                    )}
                    <div className={styles.SourceBadge}>
                        <span className={styles.SourceBadgeLabel}>Source:</span>
                        <span className={styles.SourceBadgeValue}>MCA</span>
                    </div>
                </div>
            </div>

            <div className={styles.ControlsRow}>
                {/* RECUR-27010 UAT: reuse the shared CategorySelector segmented toggle (as used by
                    Deal View / Mandate View) instead of a bespoke toggle — Standalone / Consolidated
                    text, a nature with no audited data is passed `disabled` so it renders greyed. */}
                <CategorySelector
                    categoryOptions={[AUDITED_TYPE_STANDALONE, AUDITED_TYPE_CONSOLIDATED].map((type) => ({
                        id: type,
                        label: type,
                        disabled: !(
                            auditedTypeOptionsObject.find((opt) => opt.misDisplayName === type)?.isDataAvailable ?? false
                        ),
                    }))}
                    selectedCategory={auditedType}
                    changeSelectedTab={(type) => handleConsolidationChange(type)}
                />
                <div className={styles.ControlsRight}>
                    {/* UAT RECUR-26713 (Nishank #20/#21): full width shows the inline actions; when
                        the Comments drawer narrows the page (isCompact) the secondary actions
                        collapse into a 3-dot menu beside the Auditor's Comments pill. */}
                    {isCompact ? (
                        <>
                            <AuditorCommentsPill orgId={orgId} />
                            <ThreeDotMenu
                                color="#6021b3"
                                menuItems={[
                                    { name: 'Financial Ratios', onClick: () => showAllFinancialMetrics() },
                                    { name: viewLoading ? 'Raw Data…' : 'Raw Data', onClick: () => onViewClick() },
                                    // No page-level Edit-Classification backend yet — disabled placeholder
                                    // (mirrors the disabled inline button at full width).
                                    { name: 'Edit Classification', onClick: () => {}, disabled: true },
                                ]}
                            />
                        </>
                    ) : (
                        <>
                            <div className={styles.ViewFinancialRatiosBadge} onClick={() => showAllFinancialMetrics()}>
                                <img src={ctaPurpleRight} alt="ratios" />
                                Financial Ratios
                            </div>
                            <AuditorCommentsPill orgId={orgId} />
                            <button
                                type="button"
                                className={styles.InlineTextAction}
                                onClick={() => onViewClick()}
                                disabled={viewLoading}
                            >
                                <img src={ctaPurpleRight} alt="" width={14} height={14} />
                                {viewLoading ? 'Raw Data…' : 'Raw Data'}
                            </button>
                            {/* No page-level Edit-Classification backend yet (RECUR-27010) — disabled placeholder. */}
                            <button type="button" className={styles.InlineTextAction} title="Coming soon" disabled>
                                <img src={editClassificationIcon} alt="" width={14} height={14} />
                                Edit Classification
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Row order/labels for all three statements are Figma-driven (see
                auditedStatementLayout.ts), joined to whatever classifications the API
                currently returns by `classificationName` — never the other way around.
                A SINGLE page-level spinner (the shared LoadingSpinner used by Provisional
                Financials etc.) covers the initial/nature-switch fetch — not one loader per
                statement — so the three sections render together once all are ready. */}
            {balanceSheet.loading || profitAndLoss.loading || cashFlow.loading ? (
                <LoadingSpinner color="black" height="40px" style={{ marginTop: '40px' }} />
            ) : (
                <>
                    <AuditedStatementSection
                        title={resolvedMetrics.balanceSheet || AUDITED_STATEMENT_FALLBACK_LABELS.balanceSheet}
                        layout={BALANCE_SHEET_LAYOUT}
                        tableData={balanceSheet.tableData}
                        loading={false}
                        unit={unit}
                    />
                    <AuditedStatementSection
                        title={resolvedMetrics.profitAndLoss || AUDITED_STATEMENT_FALLBACK_LABELS.profitAndLoss}
                        layout={PROFIT_AND_LOSS_LAYOUT}
                        tableData={profitAndLoss.tableData}
                        loading={false}
                        unit={unit}
                    />
                    <AuditedStatementSection
                        title={resolvedMetrics.cashFlow || AUDITED_STATEMENT_FALLBACK_LABELS.cashFlow}
                        layout={CASH_FLOW_LAYOUT}
                        tableData={cashFlow.tableData}
                        loading={false}
                        unit={unit}
                    />
                </>
            )}

            {/* "Raw Data" viewer — triggered from the actions kebab (Nishank #21). */}
            {!!viewerState && (
                <FileViewerDrawer
                    heading={`Audited Financials — ${companyName || 'Report'}`}
                    fileUrl={viewerState.url}
                    mimeType={viewerState.mimeType}
                    downloading={downloading}
                    downloadLabel="Download"
                    onDownload={onDownloadClick}
                    onClose={closeViewer}
                />
            )}
        </div>
    );
};

export default AuditedFinancialsVisualisation;
